/**
 * Tab layout (ADR 0027, glossary): which chat tabs a person shows on one
 * Account and in what order. All is always first and is not part of it.
 * OnlyFans pins and new default built-in tabs flow in once each; what the
 * person removed stays removed.
 */
import type { ListTab } from './useChatTabs'

export type BuiltinTabId = 'unread' | 'ai' | 'needs_you' | 'ignored' | 'activity' | 'suggestions' | 'active_chats'
export type TabRef = { kind: 'builtin'; id: BuiltinTabId } | { kind: 'list'; id: string; name?: string }

/** The built-in tabs this build can show, in the order the editor offers them. */
export const BUILTIN_TABS: BuiltinTabId[] = ['unread', 'ai', 'needs_you', 'active_chats', 'ignored', 'activity', 'suggestions']
/** Built-in tabs every layout starts with (and that reach saved layouts once). */
export const DEFAULT_BUILTINS: BuiltinTabId[] = ['unread', 'ai', 'needs_you', 'active_chats']
/** Default built-ins that join a saved layout at the front (right after All), not the end. */
const LEADING_BUILTINS: BuiltinTabId[] = ['unread']

export interface SavedLayout {
  tabs: TabRef[]
  /** Tabs already offered to this layout: `builtin:<id>`, `list:<id>`. */
  seen: string[]
}

export const tabKey = (t: TabRef) => `${t.kind}:${t.id}`

/** What a chip says: a built-in tab's name, or the OnlyFans list's own. */
export function tabLabel(tab: TabRef, t: (key: string) => string): string {
  return tab.kind === 'builtin' ? t(`tabs.${tab.id}`) : tab.name || `List ${tab.id}`
}

const builtin = (id: BuiltinTabId): TabRef => ({ kind: 'builtin', id })
const listTab = (l: ListTab): TabRef => ({ kind: 'list', id: l.id, name: l.name })

/** What OnlyFans and Onlymind offer every layout right now. */
function offered(pinned: ListTab[] | null): TabRef[] {
  return [...DEFAULT_BUILTINS.map(builtin), ...(pinned ?? []).map(listTab)]
}

export function defaultLayout(pinned: ListTab[] | null): TabRef[] {
  return offered(pinned)
}

/**
 * The layout to show: the default (following OnlyFans pins) when nothing is
 * saved; otherwise the saved one plus anything offered it has not seen yet,
 * with list names as OnlyFans has them now. `changed` = worth saving.
 */
export function syncLayout(saved: SavedLayout | null, pinned: ListTab[] | null): { tabs: TabRef[]; seen: string[] | null; changed: boolean } {
  if (!saved) return { tabs: defaultLayout(pinned), seen: null, changed: false }
  const names = new Map((pinned ?? []).map((p) => [p.id, p.name]))
  const tabs = saved.tabs.map((t) => (t.kind === 'list' && names.get(t.id) && names.get(t.id) !== t.name ? { ...t, name: names.get(t.id) } : t))
  const seen = new Set(saved.seen)
  const have = new Set(tabs.map(tabKey))
  // Built-ins can be offered before OnlyFans' pins are known; lists only once they are.
  const offer = pinned ? offered(pinned) : DEFAULT_BUILTINS.map(builtin)
  const fresh = offer.filter((t) => !seen.has(tabKey(t)) && !have.has(tabKey(t)))
  const grown = offer.some((t) => !seen.has(tabKey(t)))
  offer.forEach((t) => seen.add(tabKey(t)))
  const leads = (t: TabRef) => t.kind === 'builtin' && LEADING_BUILTINS.includes(t.id)
  return { tabs: [...fresh.filter(leads), ...tabs, ...fresh.filter((t) => !leads(t))], seen: [...seen], changed: grown }
}

/** What to store after an edit: the tabs, and everything offered so far as seen. */
export function savedFor(saved: SavedLayout | null, tabs: TabRef[], pinned: ListTab[] | null): SavedLayout {
  const seen = new Set(saved?.seen ?? offered(pinned).map(tabKey))
  tabs.forEach((t) => seen.add(tabKey(t)))
  return { tabs, seen: [...seen] }
}

export function addTab(tabs: TabRef[], tab: TabRef): TabRef[] {
  return tabs.some((t) => tabKey(t) === tabKey(tab)) ? tabs : [...tabs, tab]
}

export function removeTab(tabs: TabRef[], tab: TabRef): TabRef[] {
  return tabs.filter((t) => tabKey(t) !== tabKey(tab))
}

export function moveTab(tabs: TabRef[], from: number, to: number): TabRef[] {
  const next = tabs.slice()
  const [tab] = next.splice(from, 1)
  if (!tab) return tabs
  next.splice(Math.max(0, Math.min(next.length, to)), 0, tab)
  return next
}

/** Drop list tabs whose list no longer exists (only with OnlyFans' complete list of lists). */
export function pruneLists(tabs: TabRef[], lists: ListTab[]): TabRef[] {
  const ids = new Set(lists.map((l) => l.id))
  return tabs.filter((t) => t.kind !== 'list' || ids.has(t.id))
}
